// Shared dropdown option constants — used by both client and admin apps

import type { HighestEducationLevel } from './enums';

const sortAlphabeticallyWithOtherLast = (items: string[]): string[] => {
  const nonOtherItems = items.filter((item) => item !== 'Other');
  const otherItems = items.filter((item) => item === 'Other');
  return [
    ...nonOtherItems.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    ...otherItems,
  ];
};

export const NATIONALITIES = [
  'Afghan', 'Albanian', 'Algerian', 'American', 'Angolan', 'Argentine', 'Armenian',
  'Australian', 'Austrian', 'Azerbaijani', 'Bahamian', 'Bahraini', 'Bangladeshi',
  'Barbadian', 'Belarusian', 'Belgian', 'Belizean', 'Beninese', 'Bhutanese',
  'Bolivian', 'Bosnian', 'Botswanan', 'Brazilian', 'British', 'Bruneian',
  'Bulgarian', 'Burkinabe', 'Burmese', 'Burundian', 'Cambodian', 'Cameroonian',
  'Canadian', 'Cape Verdean', 'Central African', 'Chadian', 'Chilean', 'Chinese',
  'Colombian', 'Comorian', 'Congolese', 'Costa Rican', 'Croatian', 'Cuban',
  'Cypriot', 'Czech', 'Danish', 'Djiboutian', 'Dominican', 'Dutch',
  'East Timorese', 'Ecuadorian', 'Egyptian', 'Emirati', 'Equatorial Guinean',
  'Eritrean', 'Estonian', 'Ethiopian', 'Fijian', 'Filipino', 'Finnish', 'French',
  'Gabonese', 'Gambian', 'Georgian', 'German', 'Ghanaian', 'Greek', 'Grenadian',
  'Guatemalan', 'Guinean', 'Guinea-Bissauan', 'Guyanese', 'Haitian', 'Honduran',
  'Hong Kong Chinese', 'Hungarian', 'Icelander', 'Indian', 'Indonesian', 'Iranian',
  'Iraqi', 'Irish', 'Israeli', 'Italian', 'Ivorian', 'Jamaican', 'Japanese',
  'Jordanian', 'Kazakhstani', 'Kenyan', 'Kuwaiti', 'Kyrgyzstani', 'Laotian',
  'Latvian', 'Lebanese', 'Lesothan', 'Liberian', 'Libyan', 'Liechtensteiner',
  'Lithuanian', 'Luxembourger', 'Macedonian', 'Madagascan', 'Malawian', 'Malaysian',
  'Maldivian', 'Malian', 'Maltese', 'Marshallese', 'Mauritanian', 'Mauritian',
  'Mexican', 'Micronesian', 'Moldovan', 'Monegasque', 'Mongolian', 'Montenegrin',
  'Moroccan', 'Mozambican', 'Namibian', 'Nauruan', 'Nepalese', 'New Zealander',
  'Nicaraguan', 'Nigerian', 'North Korean', 'Norwegian', 'Omani', 'Pakistani',
  'Palauan', 'Palestinian', 'Panamanian', 'Papua New Guinean', 'Paraguayan',
  'Peruvian', 'Polish', 'Portuguese', 'Puerto Rican', 'Qatari', 'Romanian',
  'Russian', 'Rwandan', 'Saint Lucian', 'Salvadoran', 'Samoan', 'San Marinese',
  'Sao Tomean', 'Saudi Arabian', 'Senegalese', 'Serbian', 'Seychellois',
  'Sierra Leonean', 'Singaporean', 'Slovak', 'Slovenian', 'Solomon Islander',
  'Somali', 'South African', 'South Korean', 'South Sudanese', 'Spanish',
  'Sri Lankan', 'Sudanese', 'Surinamese', 'Swazi', 'Swedish', 'Swiss', 'Syrian',
  'Taiwanese', 'Tajikistani', 'Tanzanian', 'Thai', 'Togolese', 'Tongan',
  'Trinidadian', 'Tunisian', 'Turkish', 'Turkmen', 'Tuvaluan', 'Ugandan',
  'Ukrainian', 'Uruguayan', 'Uzbekistani', 'Vanuatuan', 'Venezuelan', 'Vietnamese',
  'Yemeni', 'Zambian', 'Zimbabwean', 'Other',
];

const UNIVERSITY_OPTIONS_RAW = [
  // ===== MALAYSIA (Public Universities) =====
  'Universiti Malaya (UM)', 'Universiti Kebangsaan Malaysia (UKM)',
  'Universiti Putra Malaysia (UPM)', 'Universiti Sains Malaysia (USM)',
  'Universiti Teknologi Malaysia (UTM)', 'Universiti Teknologi MARA (UiTM)',
  'Universiti Utara Malaysia (UUM)', 'Universiti Malaysia Perlis (UniMAP)',
  'Universiti Malaysia Kelantan (UMK)', 'Universiti Malaysia Terengganu (UMT)',
  'Universiti Teknikal Malaysia Melaka (UTeM)', 'Universiti Tun Hussein Onn Malaysia (UTHM)',
  'Universiti Malaysia Pahang (UMP)', 'Universiti Malaysia Sabah (UMS)',
  'Universiti Malaysia Sarawak (UNIMAS)', 'Universiti Pendidikan Sultan Idris (UPSI)',
  'Universiti Sains Islam Malaysia (USIM)', 'Universiti Sultan Zainal Abidin (UniSZA)',
  'Universiti Pertahanan Nasional Malaysia (UPNM)', 'Universiti Kuala Lumpur (UniKL)',
  // ===== MALAYSIA (Private Universities) =====
  'Monash University Malaysia', 'University of Nottingham Malaysia',
  'Curtin University Malaysia', 'Swinburne University of Technology Sarawak',
  'Heriot-Watt University Malaysia', 'University of Southampton Malaysia',
  'Newcastle University Medicine Malaysia', 'Multimedia University (MMU)',
  'Universiti Tenaga Nasional (UNITEN)', 'Universiti Tunku Abdul Rahman (UTAR)',
  'UCSI University', "Taylor's University", 'Sunway University',
  'Asia Pacific University (APU)', 'HELP University',
  'International Medical University (IMU)',
  'Infrastructure University Kuala Lumpur (IUKL)', 'Limkokwing University',
  'Management and Science University (MSU)', 'Nilai University', 'Perdana University',
  'SEGi University', 'Tunku Abdul Rahman University of Management and Technology (TAR UMT)',
  'INTI International University', 'KDU University College', 'Raffles University',
  'Binary University', 'Cyberjaya University College of Technology (CyberUni)',
  'Twintech International University College of Technology',
  // ===== SINGAPORE =====
  'National University of Singapore (NUS)', 'Nanyang Technological University (NTU)',
  'Singapore Management University (SMU)',
  'Singapore University of Technology and Design (SUTD)',
  'Singapore Institute of Technology (SIT)',
  'Singapore University of Social Sciences (SUSS)',
  // ===== HONG KONG =====
  'University of Hong Kong (HKU)', 'Chinese University of Hong Kong (CUHK)',
  'Hong Kong University of Science and Technology (HKUST)',
  'Hong Kong Polytechnic University (PolyU)', 'City University of Hong Kong (CityU)',
  'Hong Kong Baptist University (HKBU)', 'Lingnan University',
  'Hong Kong Metropolitan University', 'Hang Seng University of Hong Kong',
  // ===== CHINA =====
  'Tsinghua University', 'Peking University', 'Fudan University',
  'Shanghai Jiao Tong University', 'Zhejiang University', 'Nanjing University',
  'University of Science and Technology of China', 'Xiamen University',
  'Shandong University', 'Wuhan University',
  'Huazhong University of Science and Technology', 'Central South University',
  'Jilin University', 'Harbin Institute of Technology', 'Dalian University of Technology',
  'Northeastern University', 'Sun Yat-sen University', 'Sichuan University',
  'University of Electronic Science and Technology of China',
  'Southwestern Jiaotong University', 'Chongqing University',
  "Xi'an Jiao Tong University", 'Northwestern Polytechnical University',
  'Lanzhou University', 'Beijing Normal University', 'East China Normal University',
  'East China University of Science and Technology', 'Beijing Jiaotong University',
  'Beijing University of Posts and Telecommunications',
  'Beijing University of Chemical Technology', 'North China Electric Power University',
  'China University of Geosciences (Beijing)', 'Zhengzhou University',
  'Guangxi University', 'Yunnan University', 'Guizhou University',
  'Southwest University', 'Inner Mongolia University', 'Yanbian University',
  'Nanchang University', 'Ningxia University', 'Xinjiang University',
  'Qinghai University', 'Tibet University', 'Shenzhen University', 'Jinan University',
  'South China Normal University', 'Guangdong University of Technology',
  'Huazhong Agricultural University', 'Wuhan University of Technology',
  'Shanghai University', 'Shanghai University of Finance and Economics',
  // ===== INTERNATIONAL =====
  'Harvard University', 'Stanford University',
  'Massachusetts Institute of Technology (MIT)', 'University of Oxford',
  'University of Cambridge', 'Imperial College London',
  'University of California, Berkeley', 'University of California, Los Angeles (UCLA)',
  'Yale University', 'Princeton University', 'Columbia University',
  'University of Chicago', 'Cornell University', 'University of Pennsylvania',
  'Johns Hopkins University', 'Northwestern University', 'Duke University',
  'University of Michigan', 'University of Toronto', 'University of British Columbia',
  'McGill University', 'University of Melbourne', 'Australian National University',
  'University of Sydney', 'University of Queensland', 'UNSW Sydney',
  'University of Auckland', 'Seoul National University',
  'KAIST (Korea Advanced Institute of Science and Technology)',
  'Korea University', 'Yonsei University', 'University of Tokyo', 'Kyoto University',
  'Osaka University', 'Tohoku University', 'Tokyo Institute of Technology',
  'London School of Economics', 'University College London', "King's College London",
  'University of Manchester', 'University of Edinburgh', 'University of Bristol',
  'Sorbonne University (Paris)', 'INSEAD', 'ETH Zurich', 'TU Delft',
  'University of Amsterdam', 'University of Copenhagen', 'Lund University',
  'KTH Royal Institute of Technology', 'Monash University', 'RMIT University',
  'University of Technology Sydney', 'Curtin University',
  'Universidade de São Paulo (USP)', 'University of Cape Town',
  'Other',
];

export const UNIVERSITIES = sortAlphabeticallyWithOtherLast(UNIVERSITY_OPTIONS_RAW);

export const MAJOR_OPTIONS = [
  'Accounting', 'Agriculture', 'Architecture', 'Art', 'Biology', 'Biotechnology',
  'Business Administration', 'Chemistry', 'Computer Science', 'Dentistry',
  'Economics', 'Education', 'Engineering', 'Environmental Science', 'Finance',
  'History', 'Information Technology', 'Law', 'Literature', 'Marketing',
  'Mathematics', 'Medicine', 'Music', 'Nursing', 'Pharmacy', 'Physical Education',
  'Physics', 'Political Science', 'Psychology', 'Veterinary Science',
];

export const CURRENT_STATUS_OPTIONS = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'homemaker', label: 'Homemaker' },
  { value: 'studying', label: 'Studying' },
  { value: 'not_currently_employed', label: 'Not Currently Employed' },
  { value: 'retired', label: 'Retired' },
];

export const HIGHEST_EDUCATION_LEVEL_VALUES = [
  'spm',
  'uec',
  'igcse',
  'stpm',
  'a_level',
  'diploma',
  'foundation',
  'university',
  'master',
  'phd',
  'others',
] as const satisfies readonly HighestEducationLevel[];

export const EDUCATION_LEVELS_HIGHEST: Array<{ value: HighestEducationLevel; label: string }> = [
  { value: 'spm', label: 'SPM' },
  { value: 'uec', label: 'UEC' },
  { value: 'igcse', label: 'IGCSE' },
  { value: 'stpm', label: 'STPM' },
  { value: 'a_level', label: 'A-Level' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'university', label: "Bachelor's Degree" },
  { value: 'master', label: "Master's Degree" },
  { value: 'phd', label: 'PhD' },
  { value: 'others', label: 'Others' },
];

export const TEACHING_LANGUAGES = [
  { value: 'English', label: 'English' },
  { value: 'Bahasa Malaysia', label: 'Bahasa Malaysia' },
  { value: 'Chinese', label: 'Chinese' },
  { value: 'Tamil', label: 'Tamil' },
  { value: 'other', label: 'Other' },
];

export const TUTOR_EXPERIENCE_TYPES = [
  'No experience',
  'Private Tutor',
  'Current School Teacher',
  'Retired School Teacher',
  'Part-time Tuition Center Tutor',
  'Full-time Tuition Center Tutor',
  'Kindergarten Teacher',
];

export const TUITION_TYPE_OPTIONS = [
  { value: 'physical', label: 'Physical' },
  { value: 'online', label: 'Online' },
  { value: 'both', label: 'Both' },
];

export const MALAYSIAN_STATES_AND_DISTRICTS: Record<string, string[]> = {
  Johor: ['Batu Pahat', 'Johor Bahru', 'Kluang', 'Kota Tinggi', 'Kulai', 'Mersing', 'Muar', 'Pontian', 'Segamat', 'Tangkak'],
  Kedah: ['Baling', 'Bandar Baharu', 'Kota Setar', 'Kuala Muda', 'Kubang Pasu', 'Kulim', 'Langkawi', 'Padang Terap', 'Pendang', 'Pokok Sena', 'Sik', 'Yan'],
  Kelantan: ['Bachok', 'Gua Musang', 'Jeli', 'Kota Bharu', 'Kuala Krai', 'Machang', 'Pasir Mas', 'Pasir Puteh', 'Tanah Merah', 'Tumpat'],
  'Kuala Lumpur': ['Kuala Lumpur'],
  Labuan: ['Labuan'],
  Malacca: ['Alor Gajah', 'Jasin', 'Melaka Tengah'],
  'Negeri Sembilan': ['Jelebu', 'Jempol', 'Kuala Pilah', 'Port Dickson', 'Rembau', 'Seremban', 'Tampin'],
  Pahang: ['Bentong', 'Bera', 'Cameron Highlands', 'Jerantut', 'Kuantan', 'Lipis', 'Maran', 'Pekan', 'Raub', 'Rompin', 'Temerloh'],
  Penang: ['Barat Daya', 'Seberang Perai Selatan', 'Seberang Perai Tengah', 'Seberang Perai Utara', 'Timur Laut'],
  Perak: ['Bagan Datuk', 'Batang Padang', 'Hilir Perak', 'Hulu Perak', 'Kampar', 'Kerian', 'Kinta', 'Kuala Kangsar', 'Larut, Matang dan Selama', 'Manjung', 'Muallim', 'Perak Tengah'],
  Perlis: ['Perlis'],
  Putrajaya: ['Putrajaya'],
  Sabah: ['Beaufort', 'Beluran', 'Keningau', 'Kota Belud', 'Kota Kinabalu', 'Kota Marudu', 'Kuala Penyu', 'Kudat', 'Kunak', 'Lahad Datu', 'Nabawan', 'Papar', 'Penampang', 'Pitas', 'Ranau', 'Sandakan', 'Semporna', 'Sipitang', 'Tambunan', 'Tawau', 'Telupid', 'Tenom', 'Tongod', 'Tuaran'],
  Sarawak: ['Asajaya', 'Betong', 'Bintulu', 'Dalat', 'Daro', 'Julau', 'Kabong', 'Kanowit', 'Kapit', 'Kuching', 'Lawas', 'Limbang', 'Lubok Antu', 'Lundu', 'Maradong', 'Marudi', 'Matu', 'Miri', 'Mukah', 'Pakan', 'Pusa', 'Samarahan', 'Sarikei', 'Saratok', 'Sebauh', 'Selangau', 'Serian', 'Sibu', 'Simunjan', 'Song', 'Sri Aman', 'Subis', 'Tatau', 'Tanjung Manis'],
  Selangor: ['Gombak', 'Hulu Langat', 'Hulu Selangor', 'Klang', 'Kuala Langat', 'Kuala Selangor', 'Petaling', 'Sabak Bernam', 'Sepang'],
  Terengganu: ['Besut', 'Dungun', 'Hulu Terengganu', 'Kemaman', 'Kuala Nerus', 'Kuala Terengganu', 'Marang', 'Setiu'],
};
